import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import MuiListItemIcon from '@mui/material/ListItemIcon';
import MuiListItemText from '@mui/material/ListItemText';
import MuiMenu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import * as React from 'react';
import { Box } from '../box';
import { IconButton } from '../button';
import { Divider } from '../divider';
import { getIcon } from '../icon';
import { ListItem } from '../list';
import { withPaperElevation } from '../paper';
export const Menu = (props) => {
    const { actions = [], item, icon, iconProps, width, height, slotProps, disableEnforceFocus, disableAutoFocus, disableRestoreFocus } = props;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = props.open ?? Boolean(anchorEl);
    const onOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        props.onOpen?.();
    };
    const onClose = () => {
        setAnchorEl(null);
        props.onClose?.();
    };
    if (!actions.length) {
        return null;
    }
    const children = (() => {
        if (props.children) {
            return React.Children.map(props.children, (child) => React.cloneElement(child, {
                onClick: onOpen,
                children: child.props?.children
            }));
        }
        return (_jsx(IconButton, { size: iconProps?.fontSize === 'inherit'
                ? undefined
                : iconProps?.fontSize, icon: icon ?? 'IconMoreVert', iconProps: { ...iconProps, filled: open }, "aria-haspopup": "true", "aria-expanded": open ? 'true' : undefined, onClick: onOpen }));
    })();
    return (_jsxs(_Fragment, { children: [children, _jsx(MuiMenu, { anchorEl: props.anchorRef?.current ?? anchorEl, open: open, onClose: onClose, "data-role": "mui-menu", slotProps: {
                    ...slotProps,
                    paper: withPaperElevation(slotProps?.paper, 'L')
                }, disableAutoFocus: disableAutoFocus, disableEnforceFocus: disableEnforceFocus, disableRestoreFocus: disableRestoreFocus, sx: {
                    '.MuiPaper-root': {
                        width,
                        height
                    },
                    '.MuiListItem-root + .MuiDivider-root': {
                        my: 1
                    },
                    '.MuiListItemText-primary:first-letter': {
                        textTransform: 'uppercase'
                    }
                }, ...(props.anchorOrigin && {
                    anchorOrigin: props.anchorOrigin
                }), ...(props.transformOrigin && {
                    transformOrigin: props.transformOrigin
                }), children: actions
                    .filter((a) => Boolean(a))
                    .map((action, index, list) => {
                    const { type = 'MenuItem', group } = action;
                    const divider = index > 0 && group !== list[index - 1]?.group
                        ? 'top'
                        : undefined;
                    switch (type) {
                        case 'MenuItem':
                            return getMenuItem({
                                ...action,
                                item: action.item ?? item,
                                divider
                            }, onClose);
                        case 'ListItem': {
                            const { avatar, icon, iconProps, item: actionItem, onClick: actionOnClick, progress, checkbox, ...listItemProps } = action;
                            const baseProps = {
                                key: index,
                                ...listItemProps,
                                item: actionItem ?? item,
                                onClick: actionOnClick
                                    ? (item) => {
                                        actionOnClick(item);
                                        onClose();
                                    }
                                    : undefined,
                                divider
                            };
                            const slotProps = getSlotProps({
                                avatar,
                                icon,
                                iconProps,
                                progress,
                                checkbox
                            });
                            return (_jsx(ListItem, { ...baseProps, ...slotProps }));
                        }
                    }
                }) })] }));
};
export const MenuItemSection = (props) => {
    const { sx, ref, ...boxProps } = props;
    return (_jsx(Box, { ...boxProps, sx: {
            px: 2,
            py: 1,
            pointerEvents: 'none',
            textTransform: 'uppercase',
            color: (theme) => theme.palette.text.secondary,
            fontSize: (theme) => theme.typography.overline.fontSize,
            fontWeight: (theme) => theme.typography.overline.fontWeight,
            ...sx
        } }));
};
const getSlotProps = ({ avatar, icon, iconProps, progress, checkbox }) => {
    if (avatar?.src || avatar?.children) {
        return {
            avatar,
            ...(iconProps && { iconProps }),
            facilityBadge: undefined
        };
    }
    if (icon) {
        return {
            icon,
            iconProps,
            facilityBadge: undefined
        };
    }
    if (progress) {
        return {
            progress,
            facilityBadge: undefined
        };
    }
    if (checkbox !== undefined) {
        return {
            checkbox,
            facilityBadge: undefined
        };
    }
    return {
        facilityBadge: undefined
    };
};
const getMenuItem = (action, onClose) => {
    const { item, icon, primary, secondary, disabled, divider, onClick, ...rest } = action;
    const Icon = icon ? getIcon(icon) : undefined;
    if (secondary) {
        console.warn('[Menu] `secondary` is not supported in MenuItems. Please use `type="ListItem"` instead.');
    }
    return [
        divider === 'top' ? _jsx(Divider, {}, "divider") : null,
        _jsxs(MuiMenuItem, { dense: true, onClick: onClick &&
                (() => {
                    onClick?.(item);
                    onClose();
                }), disabled: disabled, ...rest, "data-role": "menu-item", children: [Icon && (_jsx(MuiListItemIcon, { children: _jsx(Icon, {}) })), typeof primary === 'string' ? (_jsx(MuiListItemText, { children: primary })) : (primary)] }, "item"),
        divider === 'bottom' ? _jsx(Divider, {}, "divider") : null
    ];
};
